import { QueryOptions, useQueries, useQueryClient } from '@tanstack/react-query';
import * as batshit from '@yornaath/batshit';
import { APITypes } from '../../primary-record-types';
import { getResourceReferenceCountsCacheKey } from './cacheKeys';
import { appAPI } from '../../utils/api';
import urls from '../../utils/urls';
import { isPresent } from 'ts-is-present';
import { memoize } from 'lodash';

export type SingleResourceReferenceCount = {
    resourceId: string;
};

export type ResourceList = {
    /**
     * The list of resource IDs.  Note that this can be capped for a very large list.
     * It's important to sort the input resource IDs by the most relevant -> least relevant
     * to ensure the most relevant resources are returned
     */
    resourceIds: string[];
};

export type UseResourceReferenceCountsArgs = (SingleResourceReferenceCount | ResourceList) & {
    medicalProfileId: string;
    isDisabled?: boolean;
};

const defaultReferenceCounts: APITypes.sharedTypes.EntityReferences = {
    condition: 0,
    allergy: 0,
    medication: 0,
    visit: 0,
    careTeam: 0,
    collaborator: 0,
    physician: null,
    attachments: 0,
    note: 0,
    comment: 0,
    imaging: 0,
    procedure: 0,
    vaccination: 0,
    lab: 0,
};

export type UseResourceReferenceCountsReturnValue = {
    isLoading: boolean;
    referenceCounts: APITypes.sharedTypes.EntityReferences;
};

function aggregateReferenceCounts(
    references: APITypes.sharedTypes.EntityReferences[],
): APITypes.sharedTypes.EntityReferences {
    const startingReferences = { ...defaultReferenceCounts };
    references.forEach((_) => {
        Object.keys(startingReferences).forEach((key) => {
            const castedKey = key as keyof APITypes.sharedTypes.EntityReferences;
            if (typeof startingReferences[castedKey] === 'number') {
                // @ts-expect-error TS7053
                startingReferences[castedKey] += _[castedKey];
            }
        });

        // `physician` is a name, not a count, so it can't be summed.  Keep the first one found.
        startingReferences.physician = startingReferences.physician || _.physician;
    });

    return startingReferences;
}

const getMedicalProfileReferenceBatcher = memoize((medicalProfileId: string) =>
    batshit.create<APITypes.medicalDataService.GetResourceReferencesListItem[], string>({
        fetcher: async (resourceIds: string[]) => {
            const response = await appAPI.post<APITypes.medicalDataService.GetResourceReferencesResponse>(
                urls.api.medicalData.resourceReferenceCounts(medicalProfileId),
                { body: { resourceIds } },
            );
            return response.references;
        },
        scheduler: batshit.windowedFiniteBatchScheduler({
            maxBatchSize: 300,
            windowMs: 10,
        }),
        resolver: (references: APITypes.medicalDataService.GetResourceReferencesResponse['references'], query) =>
            references.filter((_) => _.resourceId === query),
    }),
);

export const useResourceReferenceCounts = (
    args: UseResourceReferenceCountsArgs,
): UseResourceReferenceCountsReturnValue => {
    const queryClient = useQueryClient();
    const batcher = getMedicalProfileReferenceBatcher(args.medicalProfileId);
    const inputResourceIds = 'resourceIds' in args ? args.resourceIds : [args.resourceId];
    /**
     * Ingested data can result in a huge amount of duplicates (thousands) which causes extreme
     * slowness reconciling the query keys.  This caps it at a reasonable amount.
     *
     * It's unlikely the user will be interested in the reference counts of more than 100 resources at a time since
     * this is likely irrelevant data.  In the test data set, the "Error Inputting Data" condition type was causing the issue.
     *
     * If this becomes a problem, we can somehow find a way to speed this up or add some sort of "This number is approximated" message
     * to the user
     */
    const cappedInputResourceIds = inputResourceIds.slice(0, 100);
    const queryResourceIds = args.isDisabled ? [] : cappedInputResourceIds;
    const queries: QueryOptions<APITypes.sharedTypes.EntityReferences>[] = args.isDisabled
        ? []
        : queryResourceIds.map((resourceId) => {
              const queryKey = getResourceReferenceCountsCacheKey(args.medicalProfileId, resourceId);
              const queryState = queryClient.getQueryState(queryKey);
              return {
                  queryKey,
                  queryFn: async () => {
                      const response = await batcher.fetch(resourceId);
                      return response[0].references;
                  },
                  enabled: !queryState || queryState?.isInvalidated,
              };
          });

    const queryResults = useQueries({ queries: queries as any });

    const isLoading = args.isDisabled ? true : queryResults.some((_) => _.isLoading);
    return {
        isLoading,
        referenceCounts: isLoading
            ? defaultReferenceCounts
            : aggregateReferenceCounts(
                  queryResults.map((_) => _.data as APITypes.sharedTypes.EntityReferences).filter(isPresent),
              ),
    };
};
